'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../api/client';
import { components } from '../api/generated/schema';

type InvitationWire = components['schemas']['Invitation'];

export interface Invitation {
  id: string;
  organizationId: string;
  clerkInvitationId: string;
  email: string;
  role: 'admin' | 'member';
  status: string;
  createdAt: string;
  updatedAt: string;
}

function transformInvitation(inv: InvitationWire): Invitation {
  return {
    id: inv.id,
    organizationId: inv.organizationId,
    clerkInvitationId: inv.clerkInvitationId,
    email: inv.email,
    role: inv.role as 'admin' | 'member',
    status: inv.status,
    createdAt: inv.createdAt,
    updatedAt: inv.updatedAt,
  };
}

export interface InvitationsResponse {
  invitations: Invitation[];
  count: number;
}

/**
 * Hook for fetching the organization's pending invitations.
 * GET /api/v1/organization/invitations
 *
 * The members list (GET /members) surfaces pending rows by email but not the Clerk
 * invitation id; this provides that id so admins can revoke/resend a pending invite.
 */
export function useInvitations({
  fastPoll = false,
  limit,
  offset,
}: { fastPoll?: boolean; limit?: number; offset?: number } = {}) {
  const {
    getToken,
    isLoaded,
    isSignedIn,
    orgId: clerkOrganizationId,
  } = useAuth();

  return useQuery<InvitationsResponse>({
    queryKey: [
      'organization-invitations',
      clerkOrganizationId,
      limit ?? null,
      offset ?? null,
    ],
    enabled: isLoaded && isSignedIn === true && !!clerkOrganizationId,
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.GET(
        '/api/v1/organization/invitations',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { query: { limit, offset } },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to fetch invitations');
      }

      return {
        invitations: data.invitations.map(transformInvitation),
        count: data.count,
      };
    },
    staleTime: 30000,
    // A just-sent invite's record arrives via the Clerk webhook a beat after the
    // pending member row appears. Poll fast while that's outstanding so the row's
    // Resend/Revoke actions show within seconds, backing off to steady-state once
    // every pending member has its invitation id.
    refetchInterval: fastPoll ? 4000 : 60000,
  });
}
